import { Fragment, useEffect, useState } from 'react';
import ModuleLayout from './ModuleLayout';
import { updateOrCreate } from '../special/updateOrCreate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  API_DELETE,
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import {
  Badge,
  Box,
  Button,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TaskContent } from '../exo/project/work-package-elements/WPEToDoElement';
import useFetch from '../../api/useFetch';
import { tokens } from '../../global/theme/tokens';
import { NavLink } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { ProjectAddElementButton } from '../exo/project/ProjectAddElementButton';
import ExoDialog from '../exo/ExoDialog';
import ExoForm from '../exo/ExoForm';
import * as Yup from 'yup';
import Cookies from 'universal-cookie';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';

const DashboardAllTodos = ({ colSpan, rowSpan, debug }) => {
  const config = useConfig();
  const { apiData, setApiData } = useFetch('modules/open-tasks');
  const [isProcessing, setIsProcessing] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const initialFields = [
    { label: t('Title'), key: 'name', type: 'text' },
    {
      label: t('Project'),
      key: 'workPackage',
      type: 'projectTimeTracking',
      userId: userId,
    },
    { label: t('Description'), key: 'description', type: 'richTextEditor' },
    {
      label: t('Priority'),
      key: 'priority',
      type: 'customSelect',
      options: [
        //{ label: t("Default"), value: 0 },
        { label: t('Low'), value: 1 },
        { label: t('Medium'), value: 2 },
        { label: t('High'), value: 3 },
      ],
    },
    { label: t('Deadline'), key: 'deadline', type: 'date' },
    { label: t('File'), key: 'file', type: 'image' },
  ];

  function generateUsersFields(users) {
    return [
      { label: t('Title'), key: 'name', type: 'text' },
      {
        label: t('Project'),
        key: 'workPackage',
        type: 'projectTimeTracking',
        userId: userId,
      },
      {
        label: t('Assign users'),
        key: 'users',
        type: 'users',
        options: users,
      },
      { label: t('Description'), key: 'description', type: 'richTextEditor' },
      {
        label: t('Priority'),
        key: 'priority',
        type: 'customSelect',
        options: [
          //{ label: t("Default"), value: 0 },
          { label: t('Low'), value: 1 },
          { label: t('Medium'), value: 2 },
          { label: t('High'), value: 3 },
        ],
      },
      { label: t('Deadline'), key: 'deadline', type: 'date' },
      { label: t('File'), key: 'file', type: 'image' },
    ];
  }

  const [fields, setFields] = useState(initialFields);

  const [selectedWp, setSelectedWp] = useState(null);

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    workPackage: Yup.object().required(),
  });

  const [openNewTask, setOpenNewTask] = useState(false);

  const [showStatus, setShowStatus] = useState(0);

  function handleChangedElement(changedElement) {
    if (
      showStatus === 0 &&
      !changedElement.data.users.map((el) => el.id).includes(Number(userId))
    )
      return;

    const oldElem = apiData.data.find((el) => el.id === changedElement.data.id);

    const newTasks = updateOrCreate(apiData, {
      ...changedElement.data,
      projectId: oldElem.projectId,
      workPackageId: oldElem.workPackageId,
    });
    setApiData(newTasks);
  }

  function onDelete(element) {
    const dbTasks = apiData.data.filter((obj) => obj.id !== element.id);
    setApiData({ ...apiData, data: dbTasks });
  }
  function handleDelete(element) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${element.id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(element),
      onLoading: setIsLoading,
    });
  }

  const priorityColorMap = {
    0: colors.grey,
    1: colors.success,
    2: colors.warning,
    3: colors.error,
  };

  function handleToggleComplete(e) {
    if (isProcessing) return;
    e.status ? (e.status = 0) : (e.status = 1);

    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${e.id}`,
      method: API_PATCH(e),
      onResponse: handleChangedElement,
      onLoading: setIsProcessing,
    });
  }

  function handleStatusChange(e) {
    e.priority = (e.priority + 1) % 4;
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${e.id}`,
      method: API_PATCH(e),
      onResponse: handleChangedElement,
    });
  }

  function handleSubmit(data) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks`,
      method: API_POST({
        ...data,
        status: 0,
        wpId: data?.workPackage?.workPackage?.id,
        projectId: data?.workPackage?.project?.id,
        sortIndex: 0,
      }),
      onResponse: handleResponse,
    });
    setOpenNewTask(false);
  }

  function handleResponse(res) {
    if (
      showStatus === 1 ||
      res.data.users.map((el) => el.id).includes(Number(userId))
    ) {
      setApiData(updateOrCreate(apiData, res.data));
    }
  }

  function onStatusChange(value) {
    //Modify route or filter using value to toggle between showing all and only the users tasks
    DBRequest({
      config,
      path: `modules/open-tasks${value === 1 ? '?withUnassigned' : ''}`,
      method: API_GET,
      onResponse: setApiData,
    });
  }

  function handleChange(data) {
    if (!data?.workPackage?.workPackage && !selectedWp) return;
    setSelectedWp(data?.workPackage?.workPackage?.id);

    if (
      data?.workPackage?.project?.teamMembers &&
      data?.workPackage?.project?.teamMembers.length > 0
    ) {
      setFields(generateUsersFields(data.workPackage.project.teamMembers));
    } else setFields(initialFields);
  }

  return (
    <ModuleLayout
      colSpan={colSpan}
      rowSpan={rowSpan}
      isLoading={!apiData || isLoading}
      label={t('Project Tasks')}
    >
      <Box className="h-full overflow-auto p-2">
        <Box className="flex flex-col gap-1 pb-2">
          {
            <>
              <ToggleButtonGroup
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  '& button': {
                    padding: '3px 8px',
                  },
                }}
                size="small"
                value={showStatus}
                exclusive
                onChange={(e, value) => {
                  if (value !== showStatus && value !== null) {
                    setShowStatus(value);
                    onStatusChange(value);
                  }
                }}
                aria-label="Task Status"
              >
                <ToggleButton value={0}>
                  <Tooltip title={t('Assigned to you only')}>
                    <PersonIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={1}>
                  <Tooltip title={t('Include unassigned')}>
                    <GroupsIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <ProjectAddElementButton
                sizePx="20px"
                size="small"
                addLabel={t('Add Task')}
                onClick={() => setOpenNewTask(true)}
                sx={{
                  // border: '1px solid rgba(0, 0, 0, 0.12)', //for light mode
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                }}
              />
            </>
          }
        </Box>
        {apiData ? (
          apiData.data?.map((el, index) => (
            <Box
              className="relative flex w-full gap-1 overflow-hidden"
              key={el.id}
              sx={{
                '.del-button': {
                  width: '0px',
                  transition: '0.3s ease',
                },
                '.to-project-button': {
                  width: '0px',
                  transition: '0.3s ease',
                },
                ':hover': {
                  '.del-button': {
                    width: '30px',
                  },
                  '.to-project-button': {
                    width: '50px',
                  },
                },
              }}
            >
              <TaskContent
                key={el.id}
                className="w-full"
                element={el}
                onDelete={() => {}}
                priorityColorMap={priorityColorMap}
                onToggleComplete={() => handleToggleComplete(el)}
                isProcessing={isProcessing}
                openPopup={openPopup === el.id}
                setOpenPopup={(bool) => setOpenPopup(bool ? el.id : null)}
                handleResponse={handleChangedElement}
                handleStatusChange={() => handleStatusChange(el)}
                wpeId={el.workPackageId}
                pid={el.projectId}
                hideUsers={true}
              />
              <NavLink
                to={`/projects/management/${el.projectId}/work-package/${el.workPackageId}`}
                className="to-project-button overflow-hidden"
              >
                <Button
                  sx={{
                    padding: '0px',
                    minWidth: '0px',
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.info + 30,
                      border: `1px solid ${colors.info}80`,
                    },
                  }}
                  className=" h-full w-full"
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </Button>
              </NavLink>
              <Box className="del-button overflow-hidden">
                <Button
                  onClick={() => handleDelete(el)}
                  sx={{
                    padding: '0px',
                    minWidth: '0px',
                    border: `1px solid transparent`,
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.error + 30,
                      border: `1px solid ${colors.error}80`,
                    },
                  }}
                  className="h-full w-full"
                >
                  <DeleteIcon sx={{ fontSize: 18 }} color="error" />
                </Button>
              </Box>
            </Box>
          ))
        ) : (
          <Box className="h-full flex justify-center items-center">
            <Typography>{t('No Tasks found')}</Typography>
          </Box>
        )}
      </Box>
      <ExoDialog
        open={openNewTask}
        onClose={() => setOpenNewTask(false)}
        limitWidth
      >
        <ExoForm
          fields={fields}
          validationSchema={validationSchema}
          onChange={handleChange}
          onCancel={() => setOpenNewTask(false)}
          onSubmit={handleSubmit}
        />
      </ExoDialog>
    </ModuleLayout>
  );
};

export default DashboardAllTodos;
